<template>
  <b-form
    @submit.stop.prevent="handleSubmit"
    class="text-left"
  >
    <b-row>
      <b-col>
        <b-form-group
          :label="fieldLabels.service_id"
          label-for="work-order-charge-service_id"
        >
          <service-select
            name="work-order-charge-service_id"
            :value="form.service"
            :disabled="paid || invoiced"
            @input="onServiceChange"
            :client-id="client_id"
            client-unassigned
            :select-class="{ 'is-invalid': $v.form.service_id.$dirty && $v.form.service_id.$error, 'mb-1': true }"
          />
          <b-form-invalid-feedback
            v-if="$v.form.service_id.$dirty"
            class="work-order-charge-service_id-feedback"
          >
            <span v-if="!$v.form.service_id.required">Please enter a Service.</span>
            <span v-if="!$v.form.service_id.serverFailed">{{ serverErrors.service }}</span>
          </b-form-invalid-feedback>
          <b-form-textarea
            name="work-order-charge-description"
            v-model="$v.form.description.$model"
            placeholder="Enter a Description"
            :disabled="paid || invoiced"
            :state="$v.form.description.$dirty ? !$v.form.description.$error : null"
            rows="4"
          />
          <b-form-invalid-feedback
            v-if="$v.form.description.$dirty"
            class="work-order-charge-description-feedback"
          >
            <span v-if="!$v.form.description.required">Please enter a description.</span>
            <span v-if="!$v.form.description.serverFailed">{{ serverErrors.description }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col
        v-if="!paid"
      >
        <b-form-group
          :label="fieldLabels.user_id"
          label-for="work-order-user_id"
        >
          <user-select
            name="work-order-charge-user_id"
            :value="form.user"
            @input="onUserChange"
            :placeholder="service?.work_order_charges_require_user ? '' : 'Optional'"
            :select-class="{ 'is-invalid': $v.form.user_id.$dirty && $v.form.user_id.$error }"
            select-label="name"
            :filter-params="userSelectParams"
          />
          <b-form-invalid-feedback
            v-if="$v.form.user_id.$dirty"
            class="work-order-charge-user_id-feedback"
          >
            <span v-if="!$v.form.user_id.required">Please enter a Technician.</span>
            <span v-if="!$v.form.user_id.serverFailed">{{ serverErrors.user_id }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col>
        <b-form-group
          :label="fieldLabels.category"
          label-for="work-order-charge-category"
        >
          <category-select
            name="work-order-charge-category"
            :value="form.category"
            @input="(category) => form.category = category"
            :select-class="{ 'is-invalid': $v.form.category.$dirty && $v.form.category.$error }"
            :disabled="paid || invoiced"
          />
          <b-form-invalid-feedback
            v-if="$v.form.category.$dirty"
            class="work-order-charge-category-feedback"
          >
            <span v-if="!$v.form.category.required">Please enter a category.</span>
            <span v-if="!$v.form.category.maxLength">
              The category must be less than {{ $v.form.category.$params.maxLength.max + 1 }} characters.
            </span>
            <span v-if="!$v.form.category.serverFailed">{{ serverErrors.category }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="showSupplySelect">
      <b-col>
        <b-form-group
          :label="fieldLabels.supply_id"
          label-for="work-order-charge-supply_id"
        >
          <supply-select
            name="work-order-charge-supply_id"
            :value="form.supply"
            @input="onSupplyChange"
            :disabled="paid || invoiced"
            :select-class="{ 'is-invalid': $v.form.supply_id.$dirty && $v.form.supply_id.$error }"
          />
          <b-form-invalid-feedback
            v-if="$v.form.supply_id.$dirty"
            class="work-order-charge-supply_id-feedback"
          >
            <span v-if="!$v.form.supply_id.serverFailed">{{ serverErrors.supply }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="isFexaWorkOrder">
      <b-col>
        <b-form-group
          :label="fieldLabels.dollar_general_fexa_product_id"
          label-for="work-order-charge-dollar_general_fexa_product_id"
        >
          <dollar-general-fexa-product-select
            name="work-order-charge-dollar_general_fexa_product_id"
            :value="form.dollar_general_fexa_product_id"
            @input="onDollarGeneralFexaProductIdChange"
            :select-class="{ 'is-invalid': $v.form.dollar_general_fexa_product_id.$dirty && $v.form.dollar_general_fexa_product_id.$error }"
            :is-dollar-general="isFexaWorkOrder"
          />
          <b-form-invalid-feedback
            v-if="$v.form.dollar_general_fexa_product_id.$dirty"
            class="work-order-charge-dollar_general_fexa_product_id-feedback"
          >
            <span v-if="!$v.form.dollar_general_fexa_product_id.serverFailed">{{ serverErrors.dollar_general_fexa_product_id }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row>
      <b-col lg="4">
        <b-form-group
          :label="fieldLabels.quantity"
          label-for="work-order-charge-quantity"
          class="mb-0"
        >
          <b-form-input
            name="work-order-charge-quantity"
            v-model="$v.form.quantity.$model"
            :state="$v.form.quantity.$dirty ? !$v.form.quantity.$error : null"
            type="text"
            :disabled="paid || invoiced"
          />
          <b-form-invalid-feedback
            v-if="$v.form.quantity.$dirty"
            class="work-order-charge-quantity-feedback"
          >
            <span v-if="!$v.form.quantity.required">Please enter a quantity.</span>
            <span v-if="!$v.form.quantity.decimal">Please enter a number.</span>
            <span v-if="!$v.form.quantity.serverFailed">{{ serverErrors.quantity }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col lg="4">
        <b-form-group
          :label="costLabel"
          label-for="work-order-charge-cost"
          class="mb-0"
        >
          <b-input-group
            prepend="$"
            :class="{ 'is-invalid': $v.form.cost.$dirty && $v.form.cost.$error, 'has-warning': negativeProfitability }"
          >
            <b-form-input
              name="work-order-charge-cost"
              v-model="$v.form.cost.$model"
              :state="$v.form.cost.$dirty ? !$v.form.cost.$error : null"
              :disabled="paid"
              type="text"
            />
          </b-input-group>
          <small
            v-if="!$v.form.cost.$error"
            class="form-text text-secondary"
          >
            <money-format
              class="d-inline"
              :value="totalCostCents"
              subunits-value
            />
            total
          </small>
          <b-form-invalid-feedback
            v-if="$v.form.cost.$dirty"
            :state="$v.form.cost.$dirty ? !$v.form.cost.$error : null"
            class="work-order-charge-cost-feedback"
          >
            <span v-if="!$v.form.cost.required">Please enter a {{ costLabel }}.</span>
            <span v-if="!$v.form.cost.decimal">Please enter a number.</span>
            <span v-if="!$v.form.cost.serverFailed">{{ serverErrors.cost_cents }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col lg="4">
        <b-form-group
          :label="fieldLabels.list_cost"
          label-for="work-order-charge-list_cost"
          class="mb-0"
        >
          <template #label>
            {{ fieldLabels.list_cost }}
            <i
              class="far fa-question-circle"
              v-b-tooltip.html.hover
              title="The maximum the client agreed for us to pay for the item. <br /> Markup will be applied against the list cost."
            />
          </template>
          <b-input-group
            prepend="$"
            :class="{ 'is-invalid': $v.form.list_cost.$dirty && $v.form.list_cost.$error }"
          >
            <b-form-input
              name="work-order-charge-list_cost"
              v-model="$v.form.list_cost.$model"
              :state="$v.form.list_cost.$dirty ? !$v.form.list_cost.$error : null"
              type="text"
              placeholder="Optional"
            />
          </b-input-group>

          <b-form-invalid-feedback
            v-if="$v.form.list_cost.$dirty"
            :state="$v.form.list_cost.$dirty ? !$v.form.list_cost.$error : null"
            class="work-order-charge-list_cost-feedback"
          >
            <span v-if="!$v.form.list_cost.decimal">Please enter a number.</span>
            <span v-if="!$v.form.list_cost.serverFailed">{{ serverErrors.list_cost_cents }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row v-if="negativeProfitability">
      <b-col>
        <small
          class="warning-feedback"
        >
          Warning: the {{ $store.getters.translate('work_orders.cost', 'Cost').toLowerCase() }} of this charge is more than the {{ $store.getters.translate('work_orders.price', 'Price').toLowerCase() }}.
        </small>
      </b-col>
    </b-row>

    <b-row>
      <b-col
        cols="12"
        lg="4"
      >
        <b-form-group
          label="Markup"
          label-for="work-order-charge-markup"
        >
          <b-input-group
            append="%"
            :class="{ 'is-invalid': $v.form.markup.$dirty && $v.form.markup.$error }"
          >
            <b-form-input
              name="work-order-charge-markup"
              v-model="$v.form.markup.$model"
              placeholder="Optional"
              :disabled="paid || invoiced"
              :state="$v.form.markup.$dirty ? !$v.form.markup.$error : null"
              type="text"
            />
          </b-input-group>
          <small
            v-if="!$v.form.price.$error"
            class="form-text text-secondary"
          >
            Calculates the price
          </small>
          <b-form-invalid-feedback
            v-if="$v.form.markup.$dirty"
            :state="$v.form.markup.$dirty ? !$v.form.markup.$error : null"
            class="work-order-charge-markup-feedback"
          >
            <span v-if="!$v.form.markup.decimal">Please enter a number.</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>

      <b-col
        cols="12"
        lg="8"
      >
        <b-form-group
          :label="fieldLabels.price"
          label-for="work-order-charge-price"
          class="mb-0"
        >
          <b-input-group
            prepend="$"
            :class="{ 'is-invalid': $v.form.price.$dirty && $v.form.price.$error }"
          >
            <b-form-input
              name="work-order-charge-price"
              v-model="$v.form.price.$model"
              :disabled="invoiced"
              :state="$v.form.price.$dirty ? !$v.form.price.$error : null"
              type="text"
            />
          </b-input-group>
          <small
            v-if="!$v.form.price.$error"
            class="form-text text-secondary"
          >
            <money-format
              class="d-inline"
              :value="totalPriceCents"
              subunits-value
            />
            total
          </small>
          <b-form-invalid-feedback
            v-if="$v.form.price.$dirty"
            :state="$v.form.price.$dirty ? !$v.form.price.$error : null"
            class="work-order-charge-price-feedback"
          >
            <span v-if="!$v.form.price.required">Please enter a price.</span>
            <span v-if="!$v.form.price.decimal">Please enter a number.</span>
            <span v-if="!$v.form.price.serverFailed">{{ serverErrors.price_cents }}</span>
          </b-form-invalid-feedback>
        </b-form-group>
      </b-col>
    </b-row>

    <b-row class="mt-3">
      <b-col cols="12">
        <b-form-group label-for="work-order-charge-pay_technician_immediately">
          <b-form-checkbox
            name="work-order-charge-pay_technician_immediately"
            v-model="$v.form.pay_technician_immediately.$model"
          >
            Pay & Invoice on next billing cycle?
            <div class="font-size-sm text-secondary">
              This charge will be billed even if the work order is not yet completed
            </div>
          </b-form-checkbox>
        </b-form-group>
      </b-col>
    </b-row>

    <hr class="mt-4">

    <b-row>
      <b-col
        offset-lg="6"
        lg="3"
        order="2"
        order-lg="1"
      >
        <b-button
          variant="link-dark"
          block
          class="work-order-charge-close-button"
          @click="$emit('on-cancel')"
        >
          Cancel
        </b-button>
      </b-col>
      <b-col
        lg="3"
        order="1"
        order-lg="2"
      >
        <b-button
          class="work-order-charge-submit-button"
          block
          type="submit"
          variant="primary"
          :disabled="processing"
        >
          Save
        </b-button>
      </b-col>
    </b-row>
  </b-form>
</template>

<script>
import { required, maxLength, decimal, requiredIf } from 'vuelidate/lib/validators';
import formMixin from '@/mixins/formMixin';
import CategorySelect from '@/components/admin/workOrderCharges/CategoryButtonSelect.vue';
import DollarGeneralFexaProductSelect from '@/components/admin/integrationReferences/Fexa/QuoteItemProductSelect.vue';
import ServiceSelect from '@/components/admin/services/Select.vue';
import SupplySelect from '@/components/admin/supplies/Select.vue';
import UserSelect from '@/components/admin/users/Select.vue';
import { USER_TECHNICIAN_CATEGORY } from '@/constants/users';
import { fromCents, toCents, roundedMoney } from '@/utils/money';

export default {
  name: 'WorkOrderChargeForm',
  components: {
    CategorySelect,
    DollarGeneralFexaProductSelect,
    ServiceSelect,
    SupplySelect,
    UserSelect,
  },
  mixins: [formMixin],
  props: {
    workOrder: Object,
    client_id: Number,
    description: String,
    category: String,
    quantity: Number,
    cost: Object,
    price: Object,
    list_cost: Object,
    pay_technician_immediately: Boolean,
    service_id: Number,
    user_id: Number,
    service: Object,
    user: Object,
    supply_id: [String, Number],
    supply: Object,
    dollar_general_fexa_product_id: [String, Number],
    paid: { type: Boolean, default: false },
    invoiced: { type: Boolean, default: false },
    newForm: { type: Boolean, default: true },
  },
  data() {
    return {
      form: {
        description: this.description,
        category: this.category,
        quantity: this.quantity,
        cost: this.cost && this.cost.amount ? this.cost.amount / 100 : 0,
        cost_cents: this.cost && this.cost.amount ? this.cost.amount : 0,
        list_cost: this.list_cost && this.list_cost.amount ? this.list_cost.amount / 100 : null,
        list_cost_cents: this.list_cost && this.list_cost.amount ? this.list_cost.amount : null,
        price: this.price && this.price.amount ? this.price.amount / 100 : 0,
        price_cents: this.price && this.price.amount ? this.price.amount : 0,
        markup: null,
        pay_technician_immediately: this.pay_technician_immediately,
        service_id: this.service_id,
        user_id: this.user_id,
        service: this.service,
        user: this.user,
        supply_id: this.supply_id,
        supply: this.supply,
        dollar_general_fexa_product_id: this.dollar_general_fexa_product_id,
      },
      fieldLabels: {
        description: 'Description',
        category: 'Category',
        quantity: 'Quantity',
        cost: 'Cost',
        list_cost: this.$store.getters.translate('work_orders.list_cost', 'List Cost'),
        price: this.$store.getters.translate('work_orders.price', 'Price'),
        cost_cents: 'Cost',
        list_cost_cents: this.$store.getters.translate('work_orders.list_cost', 'List Cost'),
        price_cents: this.$store.getters.translate('work_orders.price', 'Price'),
        service_id: 'Service',
        service: 'Service',
        user: 'Technician',
        user_id: 'Technician',
        supply: 'Supply',
        supply_id: 'Supply',
        dollar_general_fexa_product_id: 'Fexa Category',
      },
      userSelectParams: { category: USER_TECHNICIAN_CATEGORY, active: true },
    };
  },
  validations: {
    form: {
      description: {
        required,
        serverFailed() {
          return !this.hasServerErrors('description');
        },
      },
      category: {
        required,
        maxLength: maxLength(255),
        serverFailed() {
          return !this.hasServerErrors('category');
        },
      },
      quantity: {
        required,
        decimal,
        serverFailed() {
          return !this.hasServerErrors('quantity');
        },
      },
      cost: {
        required,
        decimal,
        serverFailed() {
          return !this.hasServerErrors('cost_cents');
        },
      },
      list_cost: {
        decimal,
        serverFailed() {
          return !this.hasServerErrors('list_cost_cents');
        },
      },
      price: {
        required,
        decimal,
        serverFailed() {
          return !this.hasServerErrors('price_cents');
        },
      },
      markup: {
        decimal,
      },
      pay_technician_immediately: {},
      service_id: {
        required,
        serverFailed() {
          return !this.hasServerErrors('service_id');
        },
      },
      user_id: {
        required: requiredIf(function () {
          return this.form.service?.work_order_charges_require_user;
        }),
        serverFailed() {
          return !this.hasServerErrors('user_id');
        },
      },
      supply_id: {
        serverFailed() {
          return !this.hasServerErrors('supply');
        },
      },
      dollar_general_fexa_product_id: {
        serverFailed() {
          return !this.hasServerErrors('dollar_general_fexa_product_id');
        },
      },
    },
  },
  computed: {
    isFexaWorkOrder() {
      return !!this.workOrder?.dollar_general_fexa_assignment_reference_id;
    },
    showSupplySelect() {
      return this.isMaterialsCategory(this.form.category);
    },
    negativeProfitability() {
      return this.form.cost_cents > this.form.price_cents;
    },
    totalCostCents() {
      return this.form.cost_cents * this.form.quantity;
    },
    totalPriceCents() {
      return this.form.price_cents * this.form.quantity;
    },
    costLabel() {
      return this.form.user_id ? 'Tech Cost' : 'Cost';
    },
  },
  watch: {
    'form.cost': function (newCost) {
      this.form.cost_cents = toCents(newCost);
      this.updateCostChange();
    },
    'form.list_cost': function (newCost) {
      this.form.list_cost_cents = toCents(newCost);
      this.updateCostChange();
    },
    'form.price': function (newPrice) {
      this.form.price_cents = toCents(newPrice);
    },
    'form.markup': function (newMarkup) {
      this.form.markup = newMarkup;
      this.updateCostChange();
    },
    'form.category': function (newCategory) {
      if (!this.isMaterialsCategory(newCategory)) {
        this.form.supply_id = null;
        this.form.supply = null;
      }
    },
    'form.supply': function (supply) {
      this.applySupplyToForm(supply);
    },
  },
  methods: {
    isMaterialsCategory(category) {
      return (category || '').includes('material');
    },
    updateCostChange() {
      const costInput = (this.form.list_cost_cents || 0) == 0 ? this.form.cost_cents : this.form.list_cost_cents;
      if (!isNaN(costInput) && (this.form.markup || 0) != 0) {
        const newPriceCents = costInput + (costInput * (this.form.markup / 100));
        this.form.price_cents = newPriceCents;
        this.form.price = roundedMoney(fromCents(newPriceCents));
      }
    },
    onServiceChange(service) {
      this.form.service_id = service ? service.id : null;
      this.form.service = service;
      if (service?.charge_category != null) {
        this.form.category = service.charge_category;
      }
      if (service && this.newForm) {
        this.form.description = service.description;
      }
    },
    onUserChange(user) {
      this.form.user_id = user ? user.id : null;
      this.form.user = user;
    },
    onSupplyChange(supply) {
      this.form.supply_id = supply ? supply.id : null;
      this.form.supply = supply;
    },
    onDollarGeneralFexaProductIdChange(product) {
      this.form.dollar_general_fexa_product_id = product ? product.id : null;
    },
    applySupplyToForm(supply) {
      if (!supply) {
        return;
      }
      const costCents = supply.cost?.amount ?? 0;
      const priceCents = supply.price?.amount ?? 0;
      const listCostCents = supply.list_cost?.amount ?? null;

      this.form.cost_cents = costCents;
      this.form.cost = roundedMoney(fromCents(costCents));
      this.form.price_cents = priceCents;
      this.form.price = roundedMoney(fromCents(priceCents));
      this.form.list_cost_cents = listCostCents;
      this.form.list_cost = listCostCents === null ? null : roundedMoney(fromCents(listCostCents));

      if (this.newForm && !this.form.description) {
        this.form.description = supply.description;
      }
    },
  },
};
</script>
